import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

// ── Helpers ───────────────────────────────────────────────────────────────────

function apiClient() {
  const token = localStorage.getItem('token');
  return axios.create({ headers: { Authorization: `Bearer ${token}` } });
}

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-AU', {
    day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC',
  });
}

// ── Stripe card form (rendered inside an Elements provider) ───────────────────

function StripePaymentForm({ amount, clientSecret, onSuccess, onCancel }) {
  const stripe   = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError]           = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);
    setError('');
    try {
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
        clientSecret,
        { payment_method: { card: elements.getElement(CardElement) } }
      );
      if (stripeError) {
        setError(stripeError.message);
        setProcessing(false);
        return;
      }
      const res = await apiClient().post('/api/members/renew/confirm', {
        paymentIntentId: paymentIntent.id,
      });
      onSuccess(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Payment failed. Please try again.');
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ marginTop: '1.25rem' }}>
      <p style={{ fontSize: '0.875rem', color: '#374151', marginBottom: '0.75rem' }}>
        Enter your card details to complete renewal:
      </p>
      <div style={{
        border: '1.5px solid #d1d5db', borderRadius: '8px',
        padding: '0.75rem 1rem', background: '#fafafa', marginBottom: '1rem',
      }}>
        <CardElement options={{ style: { base: { fontSize: '16px', color: '#374151' } } }} />
      </div>
      {error && (
        <p style={{ color: '#dc2626', fontSize: '0.875rem', marginBottom: '0.75rem' }}>{error}</p>
      )}
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <button
          type="submit"
          disabled={!stripe || processing}
          style={{
            flex: 1, background: '#0ea5e9', color: '#fff', border: 'none',
            borderRadius: '8px', padding: '0.75rem', fontWeight: 700,
            fontSize: '0.95rem', cursor: processing ? 'not-allowed' : 'pointer',
            opacity: processing ? 0.7 : 1, transition: 'background 0.15s',
          }}
        >
          {processing ? 'Processing…' : `Pay $${parseFloat(amount).toFixed(2)}`}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={processing}
          style={{
            background: '#f1f5f9', color: '#374151', border: 'none',
            borderRadius: '8px', padding: '0.75rem 1.25rem',
            fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer',
          }}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

function RenewalPage() {
  const [info, setInfo]             = useState(null);
  const [loading, setLoading]       = useState(true);
  const [loadError, setLoadError]   = useState('');
  // step: 'info' | 'paying' | 'success'
  const [step, setStep]             = useState('info');
  const [clientSecret, setClientSecret] = useState(null);
  const [amount, setAmount]         = useState(0);
  const [stripePromise, setStripePromise] = useState(null);
  const [starting, setStarting]     = useState(false);
  const [payError, setPayError]     = useState('');

  const handleLogout = () => { localStorage.clear(); window.location.href = '/login'; };

  const user = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();
  // Admin-ish roles that reached this page via the member-view switch.
  const isStaff = ['tenantadmin', 'metaadmin', 'viewer'].includes(user.role);


  useEffect(() => {
    apiClient().get('/api/members/renewal-info')
      .then(res => {
        const d = res.data;
        // Only lapsed members should be on this page
        if (d.status !== 'lapsed') {
          window.location.href = '/dashboard';
          return;
        }
        setInfo(d);
        if (d.stripe_publishable_key) {
          setStripePromise(loadStripe(d.stripe_publishable_key));
        }
      })
      .catch(err => {
        if (err.response?.status === 401) {
          localStorage.clear();
          window.location.href = '/login';
        } else {
          setLoadError('Failed to load membership details. Please try again.');
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const handleRenewClick = async () => {
    setStarting(true);
    setPayError('');
    try {
      const res = await apiClient().post('/api/members/renew/create-payment-intent');
      if (res.data.free) {
        const confirmRes = await apiClient().post('/api/members/renew/confirm', {});
        handleSuccess(confirmRes.data);
      } else {
        setClientSecret(res.data.clientSecret);
        setAmount(res.data.amount);
        setStep('paying');
      }
    } catch (err) {
      setPayError(err.response?.data?.error || 'Failed to start renewal. Please try again.');
    } finally {
      setStarting(false);
    }
  };

  const handleSuccess = (data) => {
    setInfo(prev => ({ ...prev, new_expiry_date: data.new_expiry_date || prev?.new_expiry_date }));
    setStep('success');
    setTimeout(() => { window.location.href = '/dashboard'; }, 3000);
  };

  const primaryColour   = info?.primary_colour   || '#1a1a2e';
  const secondaryColour = info?.secondary_colour || '#d4af37';

  // ── Loading / error states ────────────────────────────────────────────────
  if (loading) return (
    <div className="dashboard">
      <div className="admin-navbar" style={{ background: '#1a1a2e' }}>
        <div className="admin-navbar-brand"><span style={{ color: '#1a1a2e', fontWeight: 'bold' }}>Me</span><span style={{ color: '#d4af37', fontWeight: 'bold' }}>Member</span></div>
      </div>
      <main className="main-content" style={{ display: 'flex', justifyContent: 'center', paddingTop: '4rem' }}>
        <p style={{ color: '#6b7280' }}>Loading…</p>
      </main>
    </div>
  );

  if (loadError) return (
    <div className="dashboard">
      <div className="admin-navbar" style={{ background: '#1a1a2e' }}>
        <div className="admin-navbar-brand"><span style={{ color: '#1a1a2e', fontWeight: 'bold' }}>Me</span><span style={{ color: '#d4af37', fontWeight: 'bold' }}>Member</span></div>
        <div className="admin-navbar-user">
          {isStaff && (
            <a className="m-viewswitch" href="/admin" title="Back to the administration dashboard">
              Admin view
            </a>
          )}
          <button onClick={handleLogout}>Sign out</button>
        </div>
      </div>
      <main className="main-content" style={{ display: 'flex', justifyContent: 'center', paddingTop: '4rem' }}>
        <div className="error-msg" style={{ maxWidth: '420px' }}>{loadError}</div>
      </main>
    </div>
  );

  const fee = parseFloat(info?.fee || 0);

  return (
    <div className="dashboard">
      {/* ── Navbar ── */}
      <div className="admin-navbar" style={{ background: primaryColour }}>
        <div className="admin-navbar-brand" style={{ color: secondaryColour }}>
          {info?.tenant_name || <><span style={{ color: '#1a1a2e', fontWeight: 'bold' }}>Me</span><span style={{ color: '#d4af37', fontWeight: 'bold' }}>Member</span></>}
        </div>
        <div className="admin-navbar-user">
          <button onClick={handleLogout}>Sign out</button>
        </div>
      </div>

      {/* ── Content ── */}
      <main className="main-content" style={{ display: 'flex', justifyContent: 'center', paddingTop: '3rem' }}>
        <div style={{ width: '100%', maxWidth: '480px' }}>

          {step === 'success' ? (
            // ── Success ──────────────────────────────────────────────────────
            <div style={{
              background: '#f0fdf4', border: '1px solid #bbf7d0',
              borderLeft: '4px solid #16a34a', borderRadius: '12px',
              padding: '2rem', textAlign: 'center',
            }}>
              <div style={{ fontSize: '2.5rem', color: '#16a34a', marginBottom: '0.75rem' }}>✓</div>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#14532d', marginBottom: '0.5rem' }}>
                You're back, {info?.first_name}!
              </h2>
              <p style={{ color: '#166534', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                Your membership has been renewed successfully.
              </p>
              <p style={{ color: '#166534', fontSize: '0.875rem' }}>
                New expiry: <strong>{fmtDate(info?.new_expiry_date)}</strong>
              </p>
              <p style={{ color: '#9ca3af', fontSize: '0.8rem', marginTop: '1.25rem' }}>
                Redirecting to your dashboard…
              </p>
            </div>

          ) : (
            <>
              {/* ── Header ─────────────────────────────────────────────────── */}
              <div style={{ textAlign: 'center', marginBottom: '1.75rem' }}>
                <h1 style={{
                  fontSize: '1.6rem', fontWeight: 700,
                  color: primaryColour, marginBottom: '0.5rem',
                }}>
                  Welcome back, {info?.first_name}!
                </h1>
                <p style={{ color: '#6b7280', fontSize: '0.9rem', lineHeight: 1.65 }}>
                  Your {info?.tenant_name} membership expired on{' '}
                  <strong style={{ color: '#dc2626' }}>{fmtDate(info?.expiry_date)}</strong>.
                  <br />
                  Renew today to continue enjoying all the benefits of membership.
                </p>
              </div>

              {/* ── Membership card ─────────────────────────────────────────── */}
              <div style={{
                background: `linear-gradient(135deg, ${primaryColour}, ${primaryColour}cc)`,
                borderRadius: '12px', padding: '1.5rem',
                color: '#fff', marginBottom: '1.5rem',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                  <div>
                    <div style={{ fontSize: '0.7rem', opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>
                      Membership Type
                    </div>
                    <div style={{ fontWeight: 700, fontSize: '1rem' }}>
                      {info?.membership_type_name || '—'}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '0.7rem', opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>
                      Renewal Fee
                    </div>
                    <div style={{ fontWeight: 700, fontSize: '1.1rem', color: secondaryColour }}>
                      {fee > 0 ? `$${fee.toFixed(2)}` : 'Free'}
                    </div>
                  </div>
                </div>
                <div style={{ paddingTop: '1rem', borderTop: '1px solid rgba(255,255,255,0.2)' }}>
                  <div style={{ fontSize: '0.7rem', opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>
                    New expiry if you renew today
                  </div>
                  <div style={{ fontWeight: 700, color: secondaryColour }}>
                    {fmtDate(info?.new_expiry_date)}
                  </div>
                </div>
              </div>

              {/* ── Error ──────────────────────────────────────────────────── */}
              {payError && (
                <div className="error-msg" style={{ marginBottom: '1rem' }}>{payError}</div>
              )}

              {/* ── Renew button ────────────────────────────────────────────── */}
              {step === 'info' && (
                <button
                  onClick={handleRenewClick}
                  disabled={starting}
                  style={{
                    width: '100%', background: '#0ea5e9', color: '#fff', border: 'none',
                    borderRadius: '10px', padding: '1rem', fontWeight: 700,
                    fontSize: '1.05rem', cursor: starting ? 'not-allowed' : 'pointer',
                    opacity: starting ? 0.7 : 1, marginBottom: '1rem',
                    transition: 'background 0.15s',
                  }}
                >
                  {starting
                    ? 'Starting…'
                    : `Renew my membership${fee > 0 ? ` — $${fee.toFixed(2)}` : ' — Free'}`}
                </button>
              )}

              {/* ── Stripe card form ────────────────────────────────────────── */}
              {step === 'paying' && clientSecret && stripePromise && (
                <Elements stripe={stripePromise} options={{ clientSecret }}>
                  <StripePaymentForm
                    amount={amount}
                    clientSecret={clientSecret}
                    onSuccess={handleSuccess}
                    onCancel={() => { setStep('info'); setPayError(''); }}
                  />
                </Elements>
              )}

              {step === 'paying' && (!clientSecret || !stripePromise) && (
                <p className="error-msg">
                  Online payments are not configured. Please contact your administrator.
                </p>
              )}

              {/* ── Footer ──────────────────────────────────────────────────── */}
              <div style={{ textAlign: 'center', marginTop: '1.75rem' }}>
                <p style={{ fontSize: '0.8rem', color: '#9ca3af', marginBottom: '0.5rem' }}>
                  Your profile and membership history have been preserved.
                </p>
                <p style={{ fontSize: '0.8rem', color: '#9ca3af' }}>
                  Not {info?.first_name}?{' '}
                  <button
                    onClick={handleLogout}
                    style={{
                      background: 'none', border: 'none', color: '#d4af37',
                      cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600,
                      textDecoration: 'underline', padding: 0,
                    }}
                  >
                    Sign out
                  </button>
                </p>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}

export default RenewalPage;
